import { useEffect, useRef, useState } from "react";
import { api, extractError, BACKEND_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { HexColorPicker } from "react-colorful";
import { toast } from "sonner";
import { Copy, Code2, ExternalLink, RefreshCw, Palette, Loader2 } from "lucide-react";

const PLATFORMS = [
  { key: "html", label: "HTML / Static", template: (s) => `<!-- Paste before </body> -->\n${s}` },
  { key: "wordpress", label: "WordPress", template: (s) => `<!-- Add to theme footer.php before </body> -->\n${s}` },
  { key: "react", label: "React", template: (s) => `// In your root layout / App.jsx\nuseEffect(() => {\n  const script = document.createElement("script");\n  script.src = "${BACKEND_URL}/api/widget/widget.js";\n  script.dataset.api = "${BACKEND_URL}/api";\n  script.async = true;\n  document.body.appendChild(script);\n}, []);` },
  { key: "next", label: "Next.js", template: (s) => `// pages/_app.js OR app/layout.js\nimport Script from "next/script";\n\n<Script src="${BACKEND_URL}/api/widget/widget.js"\n  data-api="${BACKEND_URL}/api"\n  strategy="afterInteractive" />` },
  { key: "vue", label: "Vue / Nuxt", template: (s) => `// nuxt.config.ts\napp: {\n  head: {\n    script: [{ src: "${BACKEND_URL}/api/widget/widget.js", "data-api": "${BACKEND_URL}/api", async: true }],\n  },\n}` },
  { key: "laravel", label: "Laravel Blade", template: (s) => `{{-- resources/views/layouts/app.blade.php --}}\n${s}` },
];

export default function WidgetTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [platform, setPlatform] = useState("html");
  const iframeRef = useRef(null);
  const [iframeKey, setIframeKey] = useState(0);

  useEffect(() => {
    api.get("/widget-config").then((r) => setCfg(r.data)).catch((e) => toast.error(extractError(e)));
  }, []);

  if (!cfg) return <div className="text-sm text-muted-foreground">Loading widget configuration…</div>;

  const scriptTag = `<script src="${BACKEND_URL}/api/widget/widget.js" data-api="${BACKEND_URL}/api" async></script>`;
  const copySnippet = () => { navigator.clipboard.writeText(scriptTag); toast.success("Snippet copied"); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/widget-config", cfg);
      setCfg(data);
      setIframeKey((k) => k + 1);
      toast.success("Widget updated — preview reloading");
    } catch (e) { toast.error(extractError(e)); }
    finally { setSaving(false); }
  };

  const platformCode = PLATFORMS.find((p) => p.key === platform).template(scriptTag);

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr),420px] gap-6">
      {/* Left: preview + install */}
      <div className="space-y-6 min-w-0">
        <div>
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-display font-bold text-lg">Live preview</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Reflects your saved theme</div>
            </div>
            <div className="flex items-center gap-2">
              <a href={`${BACKEND_URL}/api/widget/demo.html`} target="_blank" rel="noreferrer" className="text-xs font-mono uppercase tracking-widest text-primary hover:underline inline-flex items-center gap-1">
                Open in new tab <ExternalLink className="h-3 w-3" />
              </a>
              <Button size="sm" variant="outline" onClick={() => setIframeKey((k) => k + 1)} className="gap-1.5" data-testid="widget-preview-reload">
                <RefreshCw className="h-3 w-3" /> Reload
              </Button>
            </div>
          </div>
          <iframe
            ref={iframeRef}
            key={iframeKey}
            title="widget preview"
            src={`${BACKEND_URL}/api/widget/demo.html`}
            className="w-full h-[480px] rounded-lg border border-border/60 bg-card"
            data-testid="widget-preview-iframe"
          />
        </div>

        <div className="p-5 rounded-lg border border-border/60 bg-card">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display font-bold text-lg">Install on any website</div>
              <div className="text-xs text-muted-foreground">One line. Works everywhere. Auto-loads your theme.</div>
            </div>
            <Button size="sm" variant="ghost" onClick={copySnippet} className="gap-1.5" data-testid="copy-widget-btn">
              <Copy className="h-3 w-3" /> Copy snippet
            </Button>
          </div>
          <pre className="bg-secondary/50 rounded p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono">{scriptTag}</pre>

          <div className="mt-5">
            <Tabs value={platform} onValueChange={setPlatform}>
              <TabsList className="flex-wrap h-auto">
                {PLATFORMS.map((p) => <TabsTrigger key={p.key} value={p.key}>{p.label}</TabsTrigger>)}
              </TabsList>
              <TabsContent value={platform} className="pt-4">
                <pre className="bg-secondary/50 rounded p-3 text-xs overflow-x-auto font-mono whitespace-pre">{platformCode}</pre>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Right: theme editor */}
      <aside className="space-y-4">
        <div className="p-5 rounded-lg border border-border/60 bg-card space-y-5">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-primary" />
            <div className="font-display font-bold text-lg">Theme editor</div>
          </div>

          <Section title="Branding">
            <Field label="Bot name">
              <Input data-testid="cfg-bot-name" value={cfg.bot_name || ""} onChange={(e) => setCfg({ ...cfg, bot_name: e.target.value })} placeholder="DrkCECO" />
            </Field>
            <Field label="Bot avatar URL">
              <Input data-testid="cfg-bot-avatar" value={cfg.bot_avatar || ""} onChange={(e) => setCfg({ ...cfg, bot_avatar: e.target.value })} placeholder="https://…" />
            </Field>
            <Field label="Greeting message">
              <Textarea data-testid="cfg-greeting" value={cfg.greeting || ""} onChange={(e) => setCfg({ ...cfg, greeting: e.target.value })} rows={3} />
            </Field>
          </Section>

          <Section title="Colors">
            <ColorField label="Accent" value={cfg.accent} onChange={(v) => setCfg({ ...cfg, accent: v })} testid="cfg-accent" />
            <ColorField label="Accent text" value={cfg.accent_text} onChange={(v) => setCfg({ ...cfg, accent_text: v })} testid="cfg-accent-text" />
            <ColorField label="Panel background" value={cfg.bg} onChange={(v) => setCfg({ ...cfg, bg: v })} testid="cfg-bg" />
            <ColorField label="Panel text" value={cfg.text} onChange={(v) => setCfg({ ...cfg, text: v })} testid="cfg-text" />
          </Section>

          <Section title="Layout">
            <Field label="Position">
              <div className="flex gap-2">
                {["right", "left"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setCfg({ ...cfg, position: p })}
                    data-testid={`cfg-pos-${p}`}
                    className={`flex-1 h-9 rounded-md border text-xs font-mono uppercase tracking-widest ${cfg.position === p ? "border-primary text-primary" : "border-border/60 text-muted-foreground"}`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </Field>
            <Field label={`Offset · ${cfg.offset}px`}>
              <input type="range" min="8" max="40" value={cfg.offset} onChange={(e) => setCfg({ ...cfg, offset: parseInt(e.target.value, 10) })} className="w-full accent-primary" />
            </Field>
            <Field label={`Corner radius · ${cfg.radius}px`}>
              <input type="range" min="0" max="24" value={cfg.radius} onChange={(e) => setCfg({ ...cfg, radius: parseInt(e.target.value, 10) })} className="w-full accent-primary" />
            </Field>
          </Section>

          <Section title="Launcher">
            <ToggleRow
              label="Show label next to button"
              value={cfg.show_launcher_label}
              onChange={(v) => setCfg({ ...cfg, show_launcher_label: v })}
              testid="cfg-show-label"
            />
            {cfg.show_launcher_label && (
              <Field label="Label text">
                <Input value={cfg.launcher_label || ""} onChange={(e) => setCfg({ ...cfg, launcher_label: e.target.value })} />
              </Field>
            )}
          </Section>

          <Section title="Escalation">
            <ToggleRow
              label="Enable escalation to ticket"
              value={cfg.enable_escalation}
              onChange={(v) => setCfg({ ...cfg, enable_escalation: v })}
              testid="cfg-escalation"
            />
            <ToggleRow
              label="Require email on escalation"
              value={cfg.collect_email}
              onChange={(v) => setCfg({ ...cfg, collect_email: v })}
              testid="cfg-collect-email"
            />
          </Section>

          <Button data-testid="save-widget-cfg" onClick={save} disabled={saving} className="w-full gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & reload preview"}
          </Button>
        </div>
      </aside>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
function ToggleRow({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span>{label}</span>
      <Switch checked={value} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <Label className="text-xs mb-1.5 block">{label}</Label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="h-9 w-9 rounded-md border border-border/60 shrink-0"
          style={{ background: value }}
          aria-label={`${label} color`}
          data-testid={testid}
        />
        <Input value={value} onChange={(e) => onChange(e.target.value)} className="font-mono text-xs" />
      </div>
      {open && (
        <div className="absolute z-30 mt-2 p-2 rounded-lg border border-border/60 bg-popover shadow-2xl">
          <HexColorPicker color={value} onChange={onChange} />
          <div className="mt-2 text-right">
            <button type="button" onClick={() => setOpen(false)} className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary">Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
